import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PDialogFooterBtnActionType, PDialogFooterBtnConfig } from '@frontend-site/ui/components';
import { MfaAppEnrollment, UserMfaService, UserMfaState } from '@frontend-site/users/domain';
import { take } from 'rxjs';

export interface AuthenticatorSetupDialogData {
    companyId?: number;
}

/**
 * "Set up authenticator app": shows the server-issued QR (or the setup key), takes the 6-digit code
 * and closes with the refreshed MFA state once the server accepts it.
 */
@Component({
    selector: 'p-authenticator-setup-dialog',
    templateUrl: './authenticator-setup-dialog.component.html',
    styleUrls: ['./authenticator-setup-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthenticatorSetupDialogComponent implements OnInit {
    static readonly CODE_LENGTH = 6;

    enrollment: MfaAppEnrollment | null = null;
    code = new FormControl<string>('', { nonNullable: true });
    showManualKey = false;
    errorMsg = '';
    verifying = false;

    constructor(
        private readonly dialogRef: MatDialogRef<AuthenticatorSetupDialogComponent, UserMfaState | undefined>,
        @Inject(MAT_DIALOG_DATA) private readonly data: AuthenticatorSetupDialogData,
        private readonly userMfaService: UserMfaService,
        private readonly cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.userMfaService
            .startAppEnrollment()
            .pipe(take(1))
            .subscribe((enrollment) => {
                this.enrollment = enrollment;
                this.cd.markForCheck();
            });
    }

    get canVerify(): boolean {
        return !this.verifying && new RegExp(`^\\d{${AuthenticatorSetupDialogComponent.CODE_LENGTH}}$`).test(this.code.value);
    }

    get footerConfig(): PDialogFooterBtnConfig[] {
        return [
            {
                text: 'UserProfile.Security.AppSetup.verifyAndAdd',
                click: (): void => this.verify(),
                disabled: !this.canVerify,
                automationId: 'authenticator_verify_and_add'
            },
            { type: PDialogFooterBtnActionType.Close }
        ];
    }

    toggleManualKey(): void {
        this.showManualKey = !this.showManualKey;
    }

    verify(): void {
        if (!this.canVerify || !this.enrollment) {
            return;
        }
        this.verifying = true;
        this.errorMsg = '';
        this.userMfaService
            .confirmAppEnrollment(this.enrollment.enrollmentToken, this.code.value, this.data?.companyId)
            .pipe(take(1))
            .subscribe({
                next: (state) => this.dialogRef.close(state),
                error: (err) => {
                    // BaseController's RestErrorInfo carries the formatted BL message in `error`
                    this.errorMsg = err?.error?.error || 'UserProfile.Security.AppSetup.failed';
                    this.code.setValue('');
                    this.verifying = false;
                    this.cd.markForCheck();
                }
            });
    }
}
